import { forwardRef, useEffect, useState } from 'react';

import * as ToastPrimitives from '@radix-ui/react-toast';
import { cva } from 'class-variance-authority';
import { useTranslation } from 'react-i18next';

import Close from '@/icons/close.svg';
import ErrorIcon from '@/icons/toast-error.svg';
import SuccessIcon from '@/icons/toast-success.svg';
import { cn } from '@/utils';

const DURATION = 3000;

const ToastViewport = forwardRef(({ className, ...props }, ref) => (
	<ToastPrimitives.Viewport
		ref={ref}
		className={cn(
			'fixed top-0 z-[100] flex max-h-screen w-full flex-col-reverse p-4 sm:bottom-0 sm:right-0 sm:top-auto sm:flex-col md:max-w-[340px]',
			className
		)}
		{...props}
	/>
));
ToastViewport.displayName = ToastPrimitives.Viewport.displayName;

const toastVariants = cva(
	'group pointer-events-auto relative flex w-full items-center justify-between space-x-4 overflow-hidden rounded-md border p-6 pr-8 shadow-lg transition-all data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-top-full data-[state=open]:sm:slide-in-from-bottom-full',
	{
		variants: {
			variant: {
				default: 'border bg-background text-foreground',
				error: 'destructive group border-destructive bg-destructive text-destructive-foreground',
				success: ''
			}
		},
		defaultVariants: {
			variant: 'default'
		}
	}
);

const ToastRoot = forwardRef(({ className, variant, ...props }, ref) => {
	return (
		<ToastPrimitives.Root
			ref={ref}
			className={cn(toastVariants({ variant }), className)}
			{...props}
		/>
	);
});
ToastRoot.displayName = ToastPrimitives.Root.displayName;

const ToastClose = forwardRef(({ className, ...props }, ref) => (
	<ToastPrimitives.Close
		ref={ref}
		className={cn(
			'absolute right-7 top-7 text-black transition-opacity hover:text-foreground focus:opacity-100 focus:outline-none focus:ring-2 group-hover:opacity-100 group-[.destructive]:text-red-300 group-[.destructive]:hover:text-red-50 group-[.destructive]:focus:ring-red-400 group-[.destructive]:focus:ring-offset-red-600',
			className
		)}
		toast-close=""
		{...props}
	>
		<Close className="h-4 w-4" width={16} height={16} />
	</ToastPrimitives.Close>
));
ToastClose.displayName = ToastPrimitives.Close.displayName;

const ToastDescription = forwardRef(({ className, ...props }, ref) => (
	<ToastPrimitives.Description
		ref={ref}
		className={cn('text-xs font-semibold leading-5 text-black', className)}
		{...props}
	/>
));
ToastDescription.displayName = ToastPrimitives.Description.displayName;

export default function Toast() {
	const [open, setOpen] = useState(false);
	const [data, setData] = useState({
		message: ''
	});
	const { t } = useTranslation();
	const { message, messageKey, uuid } = data;

	const suffix = uuid ? ` Error ID: ${uuid}` : '';
	let finalMessage = messageKey ? t(messageKey) : message;
	finalMessage += suffix;

	useEffect(() => {
		function handleShowSnackbar(event) {
			setOpen(true);
			setData(event.detail);
		}

		window.addEventListener('showToast', handleShowSnackbar);

		return () => {
			window.removeEventListener('showToast', handleShowSnackbar);
		};
	}, []);

	useEffect(() => {
		if (open) {
			setTimeout(() => {
				setOpen(false);
			}, DURATION);
		}
	}, [open]);

	return (
		<ToastPrimitives.Provider swipeDirection="right">
			<ToastPrimitives.Root
				open={open}
				onOpenChange={setOpen}
				className="outline-none"
			>
				<div className="flex items-center gap-3 rounded-lg border bg-white p-4 text-sm font-semibold text-gray-700 shadow">
					{data.variant === 'success' && (
						<SuccessIcon className="text-green" />
					)}
					{data.variant === 'error' && (
						<ErrorIcon className="shrink-0 text-red" />
					)}
					{finalMessage && (
						<ToastDescription>{finalMessage}</ToastDescription>
					)}
				</div>
				<ToastClose />
			</ToastPrimitives.Root>
			<ToastViewport />
		</ToastPrimitives.Provider>
	);
}
